import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import * as services from "../../services/saleng";
import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Swal from "sweetalert2";

const PASSWORD_REGEX = /^[A-Za-z0-9_]{8,32}$/;       // a–z A–Z 0–9 _ 8–32 ตัว
const USERNAME_REGEX = /^[A-Za-z0-9_.-]{3,20}$/;     // a–z A–Z 0–9 _ . - ไม่มีช่องว่าง 3–20 ตัว

const UserFormDialog = ({
  open,
  onClose,
  onSuccess,
  initialData,
  branchId,
  branchName,
  roles = [],
  adminRoles = [],
  canManageAdminRole = false,
  isBranchScoped = false,
  isShopScoped = false,
  shopId,
  branchOptions = [],
}) => {
  const isEdit = Boolean(initialData);

  const [id, setId] = useState(null);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [roleId, setRoleId] = useState("");
  const [adminRole, setAdminRole] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");

  const [dragActive, setDragActive] = useState(false);

  const usernameError = useMemo(() => {
    if (!username) return "";
    if (!USERNAME_REGEX.test(username))
      return "ใช้ a–z, A–Z, 0–9, . _ - (3–20 ตัว) และห้ามมีช่องว่าง";
    return "";
  }, [username]);

  const passwordError = useMemo(() => {
    if (!password) return "";
    if (!PASSWORD_REGEX.test(password))
      return "รหัสผ่านต้องเป็น a–z, A–Z, 0–9 หรือ _ และยาว 8–32 ตัว (ห้ามช่องว่าง)";
    return "";
  }, [password]);

  const confirmError = useMemo(() => {
    if (!confirmPassword) return "";
    if (confirmPassword !== password) return "ยืนยันรหัสผ่านไม่ตรงกัน";
    return "";
  }, [confirmPassword, password]);

  const resetForm = () => {
    setImage(null);
    setPreview(null);
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setName("");
    setSurname("");
    setRoleId("");
    setAdminRole("");
    setSelectedBranchId("");
    setId(null);
  };

  const handleImageFile = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(String(reader.result));
      setPreview(URL.createObjectURL(file));
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageFile(file);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleRemoveImage = () => {
    setImage(null);
    setPreview(null);
  };

  // กัน spacebar ในช่องรหัสผ่าน + username
  const blockSpaceKey = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  // กันวางข้อความที่มีช่องว่างใน username (ลบช่องว่างทิ้ง)
  const handleUsernameChange = (e) => {
    const v = e.target.value.replace(/\s/g, ""); // ตัดช่องว่างทั้งหมด
    setUsername(v);
  };

  const handleSubmit = async () => {
    try {
      const u = username.trim();

      if (!u) {
        Swal.fire({
          icon: "warning",
          title: "กรอกชื่อผู้ใช้งาน",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if (!USERNAME_REGEX.test(u)) {
        Swal.fire({
          icon: "error",
          title: "รูปแบบชื่อผู้ใช้งานไม่ถูกต้อง",
          text: "ใช้ a–z, A–Z, 0–9, _ . - (3–20 ตัว) และห้ามมีช่องว่าง",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if (!password || !confirmPassword) {
        Swal.fire({
          icon: "warning",
          title: "กรอกรหัสผ่านและยืนยันรหัสผ่าน",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if (!PASSWORD_REGEX.test(password)) {
        Swal.fire({
          icon: "error",
          title: "รูปแบบรหัสผ่านไม่ถูกต้อง",
          text: "ใช้ a–z, A–Z, 0–9 หรือ _ และยาว 8–32 ตัว",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if (password !== confirmPassword) {
        Swal.fire({
          icon: "error",
          title: "ยืนยันรหัสผ่านไม่ตรงกัน",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if ((isBranchScoped || isShopScoped) && !roleId) {
        Swal.fire({
          icon: "warning",
          title: "เลือกบทบาทผู้ใช้งาน",
          customClass: { container: "my-swal" },
        });
        return;
      }
      if (isShopScoped && !selectedBranchId) {
        Swal.fire({
          icon: "warning",
          title: "เลือกสาขา",
          text: "ผู้ใช้ต้องสังกัดสาขาของร้าน",
          customClass: { container: "my-swal" },
        });
        return;
      }

      const payload = {
        username: u,
        password,
        name: name.trim(),
        lastname: surname.trim(),
        ...((isShopScoped ? selectedBranchId : branchId)
          ? { branchId: Number(isShopScoped ? selectedBranchId : branchId) }
          : {}),
        ...(roleId ? { roleId: Number(roleId) } : {}),
        ...(canManageAdminRole && adminRole ? { adminRole } : {}),
        ...((isBranchScoped || isShopScoped) && (isShopScoped ? selectedBranchId : branchId)
          ? {
              responsibleBranches: [
                Number(isShopScoped ? selectedBranchId : branchId),
              ],
            }
          : {}),
      };

      if (isEdit) {
        // TODO: ถ้ามี endpoint แก้ไข user ใส่ไว้ตรงนี้
        // await services.updateUser(id, payload)
      } else {
        if (isShopScoped) {
          await services.createUserForShop(shopId, payload);
        } else {
          await services.createUser(payload);
        }
      }

      Swal.fire({
        icon: "success",
        title: isEdit ? "แก้ไขพนักงานสำเร็จ" : "เพิ่มพนักงานสำเร็จ",
        text: isEdit
          ? "ข้อมูลพนักงานได้รับการอัปเดตแล้ว"
          : "พนักงานถูกเพิ่มเรียบร้อยแล้ว",
        customClass: { container: "my-swal" },
      }).then(() => {
        onSuccess?.();
        resetForm();
        onClose?.();
      });
    } catch (err) {
      console.error("User save failed:", err);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกพนักงานได้ กรุณาลองใหม่",
        customClass: { container: "my-swal" },
      });
    }
  };

  useEffect(() => {
    if (!initialData) return;
    setUsername(initialData.username ?? "");
    // ด้วยความปลอดภัย ไม่ preload password จาก initialData
    setPassword("");
    setConfirmPassword("");
    setName(initialData.name ?? "");
    setSurname(initialData.surname ?? "");
    setRoleId(initialData.roleId ?? "");
    setAdminRole(initialData.adminRole ?? "");
    setSelectedBranchId(initialData.branchId ?? "");
    setImage(initialData.image || null);
    setPreview(initialData.image || null);
    setId(initialData.id || null);
  }, [initialData]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        onClose?.();
      }}
      fullWidth
    >
      <DialogTitle>{isEdit ? "แก้ไขพนักงาน" : "เพิ่มพนักงาน"}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: `2px dashed ${dragActive ? "#4caf50" : "#ccc"}`,
            borderRadius: 2,
            padding: 2,
            textAlign: "center",
            backgroundColor: dragActive ? "#f1fff1" : "#fafafa",
            cursor: "pointer",
            mb: 1,
          }}
          onClick={() => document.getElementById("uploadInput")?.click()}
        >
          <Typography variant="body2" color="textSecondary">
            ลากไฟล์รูปภาพมาวาง หรือคลิกเพื่ออัปโหลด (รองรับไฟล์ .jpg .png)
          </Typography>
          {preview && (
            <Box mt={2} sx={{ position: "relative", display: "inline-block" }}>
              <img
                src={preview}
                alt="preview"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                }}
              />
              <IconButton
                onClick={handleRemoveImage}
                size="small"
                sx={{
                  position: "absolute",
                top: -8,
                right: -8,
                backgroundColor: "#fff",
                boxShadow: 1,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            id="uploadInput"
            hidden
            onChange={handleImageChange}
          />
        </Box>

        {/* Username */}
        <TextField
          label="ชื่อผู้ใช้งาน"
          fullWidth
          value={username}
          onChange={handleUsernameChange}
          onKeyDown={blockSpaceKey}
          error={!!username && !!usernameError}
          helperText={usernameError || "ใช้ a–z, A–Z, 0–9, _ . - (3–20 ตัว) ไม่มีช่องว่าง"}
          inputProps={{ maxLength: 20 }}
        />

        {/* Password */}
        <TextField
          label="รหัสผ่าน"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={blockSpaceKey}
          fullWidth
          error={!!passwordError}
          helperText={passwordError || "ใช้ a–z, A–Z, 0–9 หรือ _ (8–32 ตัว)"}
          inputProps={{ maxLength: 32 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                  aria-label="toggle password visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Confirm Password */}
        <TextField
          label="ยืนยันรหัสผ่าน"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={blockSpaceKey}
          fullWidth
          error={!!confirmError}
          helperText={confirmError || "พิมพ์ซ้ำให้ตรงกับรหัสผ่านใหม่"}
          inputProps={{ maxLength: 32 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword((show) => !show)}
                  edge="end"
                  aria-label="toggle confirm password visibility"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="ชื่อ"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="กรอกชื่อ"
        />
        <TextField
          label="นามสกุล"
          fullWidth
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          helperText="กรอกนามสกุล"
        />

        {isShopScoped && (
          <TextField
            select
            label="สาขาที่รับผิดชอบ"
            value={selectedBranchId}
            onChange={(event) => setSelectedBranchId(event.target.value)}
            fullWidth
            required
            helperText="เลือกสาขาภายในร้านที่กำลังเพิ่มผู้ใช้"
          >
            {branchOptions.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name || `สาขา ${branch.id}`}
              </MenuItem>
            ))}
          </TextField>
        )}

        {isBranchScoped && (
          <TextField
            label="สาขาที่รับผิดชอบ"
            value={branchName || branchId || ""}
            fullWidth
            InputProps={{ readOnly: true }}
            helperText="กำหนดตามสาขาของผู้สร้างและไม่สามารถแก้ไขได้"
          />
        )}

        {canManageAdminRole && (
          <TextField
            select
            label="สิทธิ์เข้า Admin"
            value={adminRole}
            onChange={(event) => setAdminRole(event.target.value)}
            fullWidth
            helperText="ไม่เลือก = ไม่มีสิทธิ์เข้า Admin และไม่กระทบ Role ของ POS"
          >
            <MenuItem value="">ไม่มีสิทธิ์เข้า Admin</MenuItem>
            {adminRoles.map((role) => (
              <MenuItem key={role.id} value={role.title}>
                {role.title}
              </MenuItem>
            ))}
          </TextField>
        )}

        {(isBranchScoped || isShopScoped) && (
          <TextField
            select
            label="บทบาท"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
            fullWidth
            required
            helperText={
              isShopScoped
                ? "เลือก Role ของผู้ใช้ POS"
                : "เลือกได้เฉพาะ Owner, Branch Manager, Cashier หรือ Scale"
            }
          >
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.title}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose?.();
          }}
        >
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={
            !username.trim() ||
            !password ||
            !confirmPassword ||
            ((isBranchScoped || isShopScoped) && !roleId) ||
            (isShopScoped && !selectedBranchId) ||
            Boolean(usernameError) ||
            Boolean(passwordError) ||
            Boolean(confirmError)
          }
        >
          {isEdit ? "บันทึกการแก้ไข" : "บันทึก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
